import {
  DATA_PLACEHOLDER,
  END_DATE_PLACEHOLDER,
  SENSOR_PLACEHOLDER,
  START_DATE_PLACEHOLDER
} from '../consts'
import {ArgumentType, FunctionDefinition} from '../types'

type FormulaNode = TextNode | GroupNode | FunctionNode
type FormulaNodeList = FormulaNode[]

interface TextNode {
  type: 'text'
  value: string
}
interface GroupNode {
  type: 'group'
  children: FormulaNodeList
}
interface FunctionNode {
  type: 'function'
  name: string
  arguments: FormulaNodeList[]
}

export function formatFormula(formula: string): string {
  const {nodes} = parseFormulaNodes(formula, 0, undefined)
  const printed = renderNodeList(nodes, 0).trim()
  return fixMulDivSpacing(printed)
}
// eslint-disable-next-line complexity
function parseFormulaNodes(
  input: string,
  startIndex: number,
  stopAt?: Set<string>
): {nodes: FormulaNodeList; index: number; stopChar?: string} {
  const nodes: FormulaNodeList = []
  let index = startIndex
  const length = input.length

  const isIdentifierStart = (ch: string) => /[A-Za-z_]/.test(ch)
  const isIdentifierPart = (ch: string) => /[A-Za-z0-9_.$]/.test(ch)
  const skipWhitespace = () => {
    while (index < length && /\s/.test(input[index])) index++
  }
  const operatorStarts = new Set(['+', '-', '*', '/', '=', '<', '>', '!'])

  while (index < length) {
    const ch = input[index]
    if (stopAt && stopAt.has(ch)) {
      return {nodes, index, stopChar: ch}
    }

    if (ch === '(') {
      index++
      const groupResult = parseFormulaNodes(input, index, new Set([')']))
      nodes.push({type: 'group', children: groupResult.nodes})
      index = groupResult.index
      if (input[index] === ')') index++
      continue
    }

    if (ch === "'" || ch === '"') {
      const quoteChar = ch
      let j = index + 1
      let buffer = quoteChar
      while (j < length) {
        if (input[j] === '\\') {
          buffer += input[j] + (input[j + 1] ?? '')
          j += 2
          continue
        }
        buffer += input[j]
        if (input[j] === quoteChar) {
          j++
          break
        }
        j++
      }
      nodes.push({type: 'text', value: buffer})
      index = j
      continue
    }

    if (/\s/.test(ch)) {
      index++
      continue
    }

    if (
      (ch === '=' || ch === '!' || ch === '<' || ch === '>') &&
      index + 1 < length &&
      input[index + 1] === '='
    ) {
      nodes.push({type: 'text', value: ch + '='})
      index += 2
      continue
    }

    if (operatorStarts.has(ch)) {
      nodes.push({type: 'text', value: ch})
      index++
      continue
    }

    if (isIdentifierStart(ch)) {
      const idStart = index
      index++
      while (index < length && isIdentifierPart(input[index])) index++
      const identifier = input.slice(idStart, index)
      skipWhitespace()
      if (input[index] === '(') {
        index++
        const argumentLists: FormulaNodeList[] = []
        if (input[index] === ')') {
          index++
        } else {
          while (index < length) {
            const argResult = parseFormulaNodes(input, index, new Set([',', ')']))
            argumentLists.push(argResult.nodes)
            index = argResult.index
            if (input[index] === ',') {
              index++
              continue
            }
            if (input[index] === ')') {
              index++
              break
            }
          }
        }
        nodes.push({type: 'function', name: identifier, arguments: argumentLists})
        continue
      } else {
        nodes.push({type: 'text', value: identifier})
        continue
      }
    }

    const tokenStart = index
    while (
      index < length &&
      !"(),'".includes(input[index]) &&
      !(stopAt && stopAt.has(input[index])) &&
      !/\s/.test(input[index]) &&
      !operatorStarts.has(input[index])
    ) {
      index++
    }

    if (index === tokenStart) {
      nodes.push({type: 'text', value: input[index]})
      index++
      continue
    }

    const token = input.slice(tokenStart, index)
    if (token.length > 0) nodes.push({type: 'text', value: token})
  }

  return {nodes, index, stopChar: undefined}
}

function nodeContainsFunction(node: FormulaNode): boolean {
  if (node.type === 'function') return true
  if (node.type === 'group') return node.children.some(nodeContainsFunction)
  return false
}
function functionHasNestedFunction(fnNode: FunctionNode): boolean {
  for (const arg of fnNode.arguments) {
    for (const n of arg) {
      if (nodeContainsFunction(n)) return true
    }
  }
  return false
}

function indent(level: number) {
  return '  '.repeat(level)
}

function renderNodeList(nodes: FormulaNodeList, indentLevel: number): string {
  const renderedParts: string[] = []
  for (let idx = 0; idx < nodes.length; idx++) {
    renderedParts.push(renderNode(nodes[idx], indentLevel))
  }
  let output = ''
  for (let i = 0; i < renderedParts.length; i++) {
    const current = renderedParts[i]
    const next = renderedParts[i + 1]
    output += current
    if (next !== undefined) {
      const lastChar = lastNonWhitespaceChar(current)
      const nextFirstChar = firstNonWhitespaceChar(next)
      if (shouldInsertSpace(lastChar, nextFirstChar)) output += ' '
    }
  }
  return output
}

function renderNode(node: FormulaNode, indentLevel: number): string {
  if (node.type === 'text') {
    const v = node.value
    if ((v.startsWith("'") && v.endsWith("'")) || (v.startsWith('"') && v.endsWith('"'))) {
      return v
    }
    return normalizeText(v)
  }
  if (node.type === 'group') {
    const inner = renderNodeList(node.children, indentLevel)
    return '(' + inner + ')'
  }
  const fnNode = node
  const hasNestedFn = functionHasNestedFunction(fnNode)
  if (!hasNestedFn) {
    const inlineArgs = fnNode.arguments
      .map((argNodes) => renderNodeList(argNodes, indentLevel))
      .join(', ')
    if (inlineArgs.length === 0) {
      return `${fnNode.name}()`
    }
    return `${fnNode.name}(${inlineArgs})`
  } else {
    const lines: string[] = []
    lines.push(`${fnNode.name}(`)
    for (let i = 0; i < fnNode.arguments.length; i++) {
      const argNodes = fnNode.arguments[i]
      const argStr = renderNodeList(argNodes, indentLevel + 1).trim()
      const line = indent(indentLevel + 1) + argStr
      lines.push(i === fnNode.arguments.length - 1 ? line : line + ',')
    }
    lines.push(indent(indentLevel) + ')')
    return lines.join('\n')
  }
}

function normalizeText(raw: string): string {
  let s = raw.replace(/\s+/g, ' ').trim()
  if (s.length === 0) return s

  s = s.replace(/==/g, '__EQEQ__')
  s = s.replace(/!=/g, '__NEQ__')
  s = s.replace(/<=/g, '__LE__')
  s = s.replace(/>=/g, '__GE__')

  s = s.replace(/([<>+\-*/=])/g, ' $1 ')

  s = s.replace(/__EQEQ__/g, ' == ')
  s = s.replace(/__NEQ__/g, ' != ')
  s = s.replace(/__LE__/g, ' <= ')
  s = s.replace(/__GE__/g, ' >= ')

  s = s.replace(/\s+/g, ' ').trim()
  return s
}

function lastNonWhitespaceChar(str: string) {
  for (let i = str.length - 1; i >= 0; i--) {
    if (!/\s/.test(str[i])) return str[i]
  }
  return ''
}
function firstNonWhitespaceChar(str: string) {
  for (let i = 0; i < str.length; i++) {
    if (!/\s/.test(str[i])) return str[i]
  }
  return ''
}
function shouldInsertSpace(prev: string, next: string) {
  if (!prev || !next) return false
  if (next === '(' && /[A-Za-z0-9_)]/.test(prev)) return false
  const isOperatorChar = /[=<>!*/+-]/.test(prev)
  const leftNeedsSpace = /[A-Za-z0-9_'")]/.test(prev) || isOperatorChar
  const rightNeedsSpace = /[A-Za-z0-9_'(]/.test(next) || /[=<>!*/+-]/.test(next)
  return leftNeedsSpace && rightNeedsSpace
}
// eslint-disable-next-line complexity
function fixMulDivSpacing(input: string): string {
  const inputLength = input.length
  let result = ''
  let index = 0
  let insideSingleQuote = false
  let insideDoubleQuote = false
  let isEscaped = false

  while (index < inputLength) {
    const currentChar = input[index]

    if (isEscaped) {
      result += currentChar
      isEscaped = false
      index++
      continue
    }

    if (currentChar === '\\') {
      result += currentChar
      isEscaped = true
      index++
      continue
    }

    if (!insideDoubleQuote && currentChar === "'") {
      insideSingleQuote = !insideSingleQuote
      result += currentChar
      index++
      continue
    }

    if (!insideSingleQuote && currentChar === '"') {
      insideDoubleQuote = !insideDoubleQuote
      result += currentChar
      index++
      continue
    }

    if (!insideSingleQuote && !insideDoubleQuote && (currentChar === '*' || currentChar === '/')) {
      while (result.length > 0) {
        const lastChar = result[result.length - 1]
        if (lastChar === ' ' || lastChar === '\t') {
          result = result.slice(0, -1)
        } else break
      }

      const previousChar = result.length > 0 ? result[result.length - 1] : ''
      if (previousChar !== '' && previousChar !== '\n' && previousChar !== '\r') {
        result += ' '
      } else if (previousChar === '\n' || previousChar === '\r') {
        result += ' '
      }

      result += currentChar

      let lookaheadIndex = index + 1
      while (
        lookaheadIndex < inputLength &&
        (input[lookaheadIndex] === ' ' || input[lookaheadIndex] === '\t')
      ) {
        lookaheadIndex++
      }

      if (
        lookaheadIndex < inputLength &&
        input[lookaheadIndex] !== '\n' &&
        input[lookaheadIndex] !== '\r'
      ) {
        result += ' '
      }

      index = lookaheadIndex
      continue
    }

    result += currentChar
    index++
  }

  return result
}

export function minifyFormula(input: string): string {
  return input
    .replace(/[\n\r\t]/g, '')
    .replace(/\s*\(\s*/g, '(')
    .replace(/\s*\)\s*/g, ')')
    .replace(/\s*,\s*/g, ',')
    .replace(/\s*([=<>!+\-*/])\s*/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

export const generateDefaultFunction = (functionDefinition: FunctionDefinition): string => {
  const args = (functionDefinition.arguments || [])
    .sort((a, b) => a.argumentOrder - b.argumentOrder)
    .map((arg) => {
      if (arg.type === ArgumentType.Sensor) return SENSOR_PLACEHOLDER
      if (arg.type === ArgumentType.Fix) {
        if (arg.name === 'start_date') return START_DATE_PLACEHOLDER
        if (arg.name === 'end_date') return END_DATE_PLACEHOLDER
        if (arg.name === 'data') return DATA_PLACEHOLDER
      }
      if (arg.type === ArgumentType.Enum) return `'${arg.name}'`
      return arg.name
    })
  return `${functionDefinition.name}(${args.join(', ')})`
}
